import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { Stage, Layer, Rect, Line, Circle, Transformer } from 'react-konva';
import { MAX_CANVAS_SCALE, MIN_CANVAS_SCALE, useCanvasStore } from '../../store/canvasStore';
import { useEditorStore } from '../../store/editorStore';
import { undoEditor } from '../../store/historyActions';
import {
  pixelsToMetres,
  metresToPixels,
} from '../../canvasConstants';
import { pointAtDistance, resolveDrawingPoint, type GuideLine, type Pt } from '../../drawingGuides';
import { THEMES } from '../../floorplanTheme';
import { SYMBOL_BY_ID, familyOf, symbolAnchorsToWall, symbolSupportsStairDirection } from '../../symbols';
import { copySelection, pasteSelection } from '../../store/clipboardActions';
import GridLayer from './GridLayer';
import RoomShape from './RoomShape';
import WallShape from './WallShape';
import SymbolShape from './SymbolShape';
import DimensionShape from './DimensionShape';
import { RoomSizePrompt, WallLengthControl, WallLinePicker } from './DrawAssist';

function getCanvasPoint(stage: any, scale: number, panX: number, panY: number): Pt | null {
  const pos = stage?.getPointerPosition();
  if (!pos) return null;
  return { x: (pos.x - panX) / scale, y: (pos.y - panY) / scale };
}

/**
 * The transformer resets anchor size on every update, then calls this.
 * Reapply the rotate handle each time so it stays a visible red ↻.
 *
 * The hit path must use fillStrokeShape. A plain ctx.fill() paints the
 * current fillStyle, so Konva never records this handle and the drag is
 * ignored (the click falls through to the canvas).
 */
function styleRotateAnchor(anchor: any) {
  if (!anchor.hasName?.('rotater')) return;
  anchor.width(28);
  anchor.height(28);
  anchor.offsetX(14);
  anchor.offsetY(14);
  anchor.hitFunc((ctx: any, shape: any) => {
    ctx.beginPath();
    ctx.arc(14, 14, 16, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStrokeShape(shape);
  });
  anchor.sceneFunc((ctx: CanvasRenderingContext2D) => {
    ctx.beginPath();
    ctx.arc(14, 14, 13, 0, Math.PI * 2);
    ctx.fillStyle = '#FF4C4C';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 18px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('↻', 14, 15);
  });
}

function isTransformerHandle(target: any): boolean {
  let node = target;
  while (node) {
    const name = typeof node.name === 'function' ? node.name() : '';
    if (String(name).includes('_anchor') || node.className === 'Transformer') return true;
    node = typeof node.getParent === 'function' ? node.getParent() : null;
  }
  return false;
}

const TOOL_HINTS: Record<string, string> = {
  select: 'Drag empty space to move the plan. Drag the red ↻ to rotate a room or symbol.',
  room: 'Click to type width and height, or drag to draw. Edges line up with alignment guides.',
  wall: 'Click the first point, then type a length and press Enter. The cursor only aims the line. Hold Shift for 45°.',
  polygon: 'Coming soon',
  dimension: 'Drag between two points to measure. Snaps to corners and alignment guides.',
  text: 'Coming soon',
  symbol: 'Click the canvas to place the symbol. Choose another from the panel on the right.',
  eraser: 'Click a shape to delete it.',
};

interface DrawingCanvasProps {
  onCanvasChange?: () => void;
}

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({ onCanvasChange }) => {
  const stageRef = useRef<any>(null);
  const trRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ width: 1200, height: 800 });
  const { shapes, scale, panX, panY, setScale, setPan, setViewport, addShape, pushHistory } = useCanvasStore();
  const {
    gridVisible,
    activeTool,
    snapEnabled,
    canvasTheme,
    selectedIds,
    setSelectedIds,
    setActiveTool,
    pendingSymbolId,
    setPendingSymbol,
    setLastSymbol,
    wallThickness,
    setWallThickness,
    draftWallPoints: wallDraw,
    setDraftWallPoints: setWallDraw,
  } = useEditorStore();
  const theme = THEMES[canvasTheme];
  const [roomDraw, setRoomDraw] = useState<{ start: Pt; current: Pt } | null>(null);
  const [roomPrompt, setRoomPrompt] = useState<Pt | null>(null);
  const [dimDraw, setDimDraw] = useState<{ start: Pt; current: Pt } | null>(null);
  const [cursorPos, setCursorPos] = useState<Pt | null>(null);
  const [wallLengthDraft, setWallLengthDraft] = useState('');
  const [spacePan, setSpacePan] = useState(false);
  const [shiftDown, setShiftDown] = useState(false);
  const shiftRef = useRef(false);
  const roomDrawRef = useRef(roomDraw);
  roomDrawRef.current = roomDraw;
  const dimDrawRef = useRef(dimDraw);
  dimDrawRef.current = dimDraw;

  const snapPts = useMemo(() => {
    const out: Pt[] = [];
    for (const s of shapes) {
      if (s.type === 'room' && s.width != null && s.height != null) {
        out.push(
          { x: s.x, y: s.y },
          { x: s.x + s.width, y: s.y },
          { x: s.x, y: s.y + s.height },
          { x: s.x + s.width, y: s.y + s.height }
        );
      } else if ((s.type === 'wall' || s.type === 'dimension') && s.points) {
        for (let i = 0; i + 1 < s.points.length; i += 2) out.push({ x: s.points[i], y: s.points[i + 1] });
      }
    }
    return out;
  }, [shapes]);

  const anchors = useMemo(() => {
    const earlier = wallDraw && wallDraw.length > 1 ? wallDraw.slice(0, -1) : [];
    return [...snapPts, ...earlier];
  }, [snapPts, wallDraw]);

  const resolve = useCallback(
    (raw: Pt, prev: Pt | null, ortho: boolean) =>
      resolveDrawingPoint({
        raw,
        prev,
        anchors,
        scale,
        ortho,
        snapGrid: snapEnabled,
      }),
    [anchors, scale, snapEnabled]
  );

  const updateSize = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setStageSize({ width: rect.width, height: rect.height });
    setViewport(rect.width, rect.height);
  }, [setViewport]);

  useEffect(() => {
    updateSize();
    const ro = new ResizeObserver(updateSize);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [updateSize]);

  useEffect(() => {
    if (activeTool !== 'room') {
      setRoomDraw(null);
      setRoomPrompt(null);
    }
    if (activeTool !== 'wall') setWallDraw(null);
    if (activeTool !== 'dimension') setDimDraw(null);
  }, [activeTool, setWallDraw]);

  useEffect(() => () => useEditorStore.getState().setDraftWallPoints(null), []);

  const finishWall = useCallback(() => {
    const pts = useEditorStore.getState().draftWallPoints;
    if (!pts || pts.length < 2) return;
    let next = [...pts];
    if (next.length >= 3) {
      const last = next[next.length - 1];
      const prev = next[next.length - 2];
      if (Math.hypot(last.x - prev.x, last.y - prev.y) < 8) next = next.slice(0, -1);
    }
    if (next.length < 2) {
      setWallDraw(null);
      return;
    }
    const flat = next.flatMap((p) => [p.x, p.y]);
    addShape({
      id: crypto.randomUUID(),
      type: 'wall',
      x: 0,
      y: 0,
      points: flat,
      thickness: useEditorStore.getState().wallThickness,
      stroke: theme.wall.stroke,
    });
    pushHistory();
    onCanvasChange?.();
    setWallDraw(null);
  }, [theme, addShape, pushHistory, onCanvasChange, setWallDraw]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const el = e.target instanceof HTMLElement ? e.target : null;
      const wallLength = el?.id === 'wall-length-input';
      const typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (typing && !wallLength) return;
      if (wallLength && (el as HTMLInputElement).value) return;
      if (roomPrompt) {
        setRoomPrompt(null);
        return;
      }
      const draft = useEditorStore.getState().draftWallPoints;
      if (draft && draft.length >= 2) finishWall();
      else setWallDraw(null);
      setRoomDraw(null);
      setDimDraw(null);
      setSelectedIds([]);
      if (pendingSymbolId) {
        setPendingSymbol(null);
        setActiveTool('select');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [roomPrompt, finishWall, setWallDraw, setSelectedIds, pendingSymbolId, setPendingSymbol, setActiveTool]);

  useEffect(() => {
    const isTyping = (el: EventTarget | null) =>
      el instanceof HTMLElement && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    const onDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        shiftRef.current = true;
        setShiftDown(true);
      }
      if (e.code === 'Space' && !isTyping(e.target)) {
        e.preventDefault();
        setSpacePan(true);
      }
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        shiftRef.current = false;
        setShiftDown(false);
      }
      if (e.code === 'Space') setSpacePan(false);
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);

  useEffect(() => {
    const isTypingElsewhere = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.id === 'wall-length-input') return false;
      return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
    };
    const onKey = (e: KeyboardEvent) => {
      if (isTypingElsewhere(e.target)) return;
      if ((e.metaKey || e.ctrlKey) && !e.altKey) {
        const key = e.key.toLowerCase();
        if (key === 'c') {
          if (copySelection()) e.preventDefault();
          return;
        }
        if (key === 'v') {
          e.preventDefault();
          if (pasteSelection()) onCanvasChange?.();
          return;
        }
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const typingLength = e.target instanceof HTMLElement && e.target.id === 'wall-length-input';
      if (!(wallDraw && wallDraw.length) || typingLength) return;
      if (!/^[0-9.]$/.test(e.key)) return;
      e.preventDefault();
      setWallLengthDraft((current) => (e.key === '.' && current.includes('.') ? current : current + e.key));
      requestAnimationFrame(() => document.getElementById('wall-length-input')?.focus());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [wallDraw, onCanvasChange]);

  useEffect(() => {
    const tr = trRef.current;
    const stage = stageRef.current;
    if (!tr || !stage) return;
    if (activeTool !== 'select') {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
      return;
    }
    const nodes = selectedIds
      .map((id) => stage.findOne('#' + id))
      .filter((n: any) => n && typeof n.name === 'function' && n.name() === 'transformable');
    tr.nodes(nodes);
    tr.getLayer()?.batchDraw();
  }, [selectedIds, shapes, activeTool, scale, panX, panY]);

  const handleWheel = useCallback(
    (e: any) => {
      e.evt.preventDefault();
      const stage = stageRef.current;
      if (!stage) return;
      if (!e.evt.ctrlKey && !e.evt.metaKey) {
        setPan(panX - e.evt.deltaX, panY - e.evt.deltaY);
        return;
      }
      const scaleBy = 1.1;
      const oldScale = scale;
      const pointer = stage.getPointerPosition();
      if (!pointer) return;
      const mousePointTo = { x: (pointer.x - panX) / oldScale, y: (pointer.y - panY) / oldScale };
      const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;
      const clamped = Math.min(MAX_CANVAS_SCALE, Math.max(MIN_CANVAS_SCALE, newScale));
      setScale(clamped);
      setPan(pointer.x - mousePointTo.x * clamped, pointer.y - mousePointTo.y * clamped);
    },
    [scale, panX, panY, setScale, setPan]
  );

  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });

  const commitRoom = useCallback(
    (x: number, y: number, w: number, h: number) => {
      const id = crypto.randomUUID();
      const roomCount = useCanvasStore.getState().shapes.filter((s) => s.type === 'room').length + 1;
      addShape({
        id,
        type: 'room',
        x,
        y,
        width: Math.max(20, w),
        height: Math.max(20, h),
        label: `Room ${roomCount}`,
        fill: 'transparent',
        opacity: 1,
      });
      pushHistory();
      onCanvasChange?.();
      setSelectedIds([id]);
      setRoomPrompt(null);
      setRoomDraw(null);
    },
    [addShape, pushHistory, onCanvasChange, setSelectedIds]
  );

  const placeSymbol = useCallback(
    (at: Pt) => {
      const symbolId = pendingSymbolId ?? useEditorStore.getState().lastSymbolId;
      const def = SYMBOL_BY_ID[symbolId];
      if (!def) return;
      const wpx = metresToPixels(def.widthM);
      const hpx = metresToPixels(def.heightM);
      const id = crypto.randomUUID();
      // Hinged doors swing up from the wall, so the click is the opening, not the box centre.
      const openingOnBaseline = symbolAnchorsToWall(symbolId);
      addShape({
        id,
        type: 'symbol',
        symbolId,
        x: at.x - wpx / 2,
        y: openingOnBaseline ? at.y - hpx : at.y - hpx / 2,
        width: wpx,
        height: hpx,
        rotation: 0,
        ...(symbolSupportsStairDirection(symbolId) ? { stairDirection: 'up' as const } : {}),
      });
      pushHistory();
      onCanvasChange?.();
      setLastSymbol(symbolId);
      setPendingSymbol(null);
      setActiveTool('select');
      setSelectedIds([id]);
    },
    [pendingSymbolId, addShape, pushHistory, onCanvasChange, setLastSymbol, setPendingSymbol, setActiveTool, setSelectedIds]
  );

  const handleMouseDown = useCallback(
    (e: any) => {
      if (e.evt.button === 1 || (e.evt.button === 0 && spacePan)) {
        setIsPanning(true);
        panStart.current = { x: e.evt.clientX - panX, y: e.evt.clientY - panY };
        return;
      }
      if (e.evt.button !== 0) return;
      // The red rotate handle (and the resize squares) must not deselect or pan.
      if (isTransformerHandle(e.target)) return;
      const pt = getCanvasPoint(stageRef.current, scale, panX, panY);
      if (!pt) return;
      const ortho = shiftRef.current;

      if (activeTool === 'select') {
        const clickedEmpty = e.target === e.target?.getStage?.();
        if (clickedEmpty) {
          setSelectedIds([]);
          setIsPanning(true);
          panStart.current = { x: e.evt.clientX - panX, y: e.evt.clientY - panY };
        }
        return;
      }
      if (activeTool === 'symbol') {
        placeSymbol(resolve(pt, null, false).point);
        return;
      }
      if (activeTool === 'room') {
        setRoomPrompt(null);
        const start = resolve(pt, null, false).point;
        setRoomDraw({ start, current: start });
        return;
      }
      if (activeTool === 'wall') {
        setWallDraw((prev) => {
          const last = prev && prev.length ? prev[prev.length - 1] : null;
          const next = resolve(pt, last, ortho).point;
          return prev ? [...prev, next] : [next];
        });
        return;
      }
      if (activeTool === 'dimension') {
        const start = resolve(pt, null, ortho).point;
        setDimDraw({ start, current: start });
      }
    },
    [panX, panY, activeTool, scale, spacePan, setSelectedIds, placeSymbol, resolve, setWallDraw]
  );

  const handleMouseMove = useCallback(
    (e: any) => {
      const pt = getCanvasPoint(stageRef.current, scale, panX, panY);
      setCursorPos(pt ?? null);

      if (isPanning) {
        setPan(e.evt.clientX - panStart.current.x, e.evt.clientY - panStart.current.y);
        return;
      }
      if (!pt) return;
      const ortho = shiftRef.current;
      if (roomDrawRef.current && activeTool === 'room') {
        setRoomDraw((d) => (d ? { ...d, current: resolve(pt, null, false).point } : null));
      }
      if (dimDrawRef.current && activeTool === 'dimension') {
        setDimDraw((d) => (d ? { ...d, current: resolve(pt, d.start, ortho).point } : null));
      }
    },
    [isPanning, setPan, activeTool, scale, panX, panY, resolve]
  );

  const handleDblClick = useCallback(() => {
    const draft = useEditorStore.getState().draftWallPoints;
    if (draft && draft.length >= 2) finishWall();
  }, [finishWall]);

  const handleMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      return;
    }
    const draw = roomDrawRef.current;
    if (draw && activeTool === 'room') {
      roomDrawRef.current = null;
      setRoomDraw(null);
      const screenDist = Math.hypot(draw.current.x - draw.start.x, draw.current.y - draw.start.y) * scale;
      if (screenDist < 6) {
        setRoomPrompt(draw.start);
        return;
      }
      const x = Math.min(draw.start.x, draw.current.x);
      const y = Math.min(draw.start.y, draw.current.y);
      const w = Math.abs(draw.current.x - draw.start.x);
      const h = Math.abs(draw.current.y - draw.start.y);
      commitRoom(x, y, w, h);
      return;
    }
    const dim = dimDrawRef.current;
    if (dim && activeTool === 'dimension') {
      dimDrawRef.current = null;
      setDimDraw(null);
      if (Math.hypot(dim.current.x - dim.start.x, dim.current.y - dim.start.y) > 6) {
        addShape({
          id: crypto.randomUUID(),
          type: 'dimension',
          x: 0,
          y: 0,
          points: [dim.start.x, dim.start.y, dim.current.x, dim.current.y],
        });
        pushHistory();
        onCanvasChange?.();
      }
    }
  }, [isPanning, activeTool, scale, commitRoom, addShape, pushHistory, onCanvasChange]);

  const drawingResolved =
    cursorPos && !isPanning && (activeTool === 'wall' || activeTool === 'room' || activeTool === 'dimension')
      ? resolve(
          cursorPos,
          activeTool === 'wall' && wallDraw?.length
            ? wallDraw[wallDraw.length - 1]
            : activeTool === 'dimension' && dimDraw
              ? dimDraw.start
              : null,
          shiftDown && activeTool !== 'room'
        )
      : null;
  const guides: GuideLine[] = drawingResolved?.guides ?? [];

  const typedWallMetres = (() => {
    const n = parseFloat(wallLengthDraft);
    return Number.isFinite(n) && n > 0 && n <= 200 ? n : null;
  })();

  const wallPreviewPoint: Pt | null =
    wallDraw?.length && drawingResolved
      ? typedWallMetres
        ? pointAtDistance(wallDraw[wallDraw.length - 1], drawingResolved.point, metresToPixels(typedWallMetres))
        : drawingResolved.point
      : null;

  const commitTypedLength = (metres: number) => {
    if (!wallDraw?.length) return;
    const last = wallDraw[wallDraw.length - 1];
    const next =
      wallPreviewPoint ?? pointAtDistance(last, { x: last.x + 1, y: last.y }, metresToPixels(metres));
    setWallDraw([...wallDraw, next]);
    setWallLengthDraft('');
  };

  const roomShapes = shapes.filter((s) => s.type === 'room');
  const wallShapes = shapes.filter((s) => s.type === 'wall');
  const symbolShapes = shapes.filter((s) => s.type === 'symbol');
  const dimensionShapes = shapes.filter((s) => s.type === 'dimension');
  const isDrawingTool = activeTool === 'room' || activeTool === 'wall' || activeTool === 'dimension' || activeTool === 'symbol';
  const canvasCursor = isPanning
    ? 'grabbing'
    : spacePan
      ? 'grab'
      : isDrawingTool
        ? 'crosshair'
        : 'default';

  const liveSegmentM =
    typedWallMetres ??
    (wallDraw?.length && wallPreviewPoint
      ? pixelsToMetres(
          Math.hypot(
            wallPreviewPoint.x - wallDraw[wallDraw.length - 1].x,
            wallPreviewPoint.y - wallDraw[wallDraw.length - 1].y
          )
        )
      : 0);

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-hidden relative"
      style={{ backgroundColor: theme.canvas.background, cursor: canvasCursor }}
    >
      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        scaleX={scale}
        scaleY={scale}
        x={panX}
        y={panY}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDblClick={handleDblClick}
      >
        <Layer>
          {gridVisible && (
            <GridLayer
              width={stageSize.width}
              height={stageSize.height}
              scale={scale}
              panX={panX}
              panY={panY}
              gridStroke={theme.grid.stroke}
            />
          )}
        </Layer>
        <Layer>
          {wallShapes.map((shape) => (
            <WallShape key={shape.id} shape={shape} onCanvasChange={onCanvasChange} />
          ))}
          {roomShapes.map((shape) => (
            <RoomShape key={shape.id} shape={shape} onCanvasChange={onCanvasChange} />
          ))}
          {symbolShapes.map((shape) => (
            <SymbolShape key={shape.id} shape={shape} onCanvasChange={onCanvasChange} />
          ))}
          {dimensionShapes.map((shape) => (
            <DimensionShape key={shape.id} shape={shape} onCanvasChange={onCanvasChange} />
          ))}

          {guides.map((g, i) => (
            <Line
              key={`guide-${i}`}
              points={[g.x1, g.y1, g.x2, g.y2]}
              stroke={theme.guide.stroke}
              strokeWidth={1.25 / scale}
              dash={[7 / scale, 5 / scale]}
              listening={false}
            />
          ))}

          {roomDraw && activeTool === 'room' && (() => {
            const x = Math.min(roomDraw.start.x, roomDraw.current.x);
            const y = Math.min(roomDraw.start.y, roomDraw.current.y);
            const w = Math.max(2, Math.abs(roomDraw.current.x - roomDraw.start.x));
            const h = Math.max(2, Math.abs(roomDraw.current.y - roomDraw.start.y));
            return (
              <>
                <Rect x={x} y={y} width={w} height={h} fill={theme.preview.fill} stroke={theme.preview.stroke} strokeWidth={2.5} listening={false} />
                {w < 4 && h < 4 && <Circle x={roomDraw.start.x} y={roomDraw.start.y} radius={6} fill={theme.preview.dot} listening={false} />}
              </>
            );
          })()}

          {wallDraw && activeTool === 'wall' && wallPreviewPoint && (() => {
            const preview = wallPreviewPoint;
            const pts = [...wallDraw, preview];
            const flat = pts.flatMap((p) => [p.x, p.y]);
            return (
              <>
                {flat.length >= 4 && (
                  <Line
                    points={flat}
                    stroke={theme.preview.stroke}
                    strokeWidth={wallThickness}
                    opacity={0.5}
                    lineCap="butt"
                    lineJoin="miter"
                    listening={false}
                  />
                )}
                {wallDraw.map((p, i) => (
                  <Circle key={i} x={p.x} y={p.y} radius={4} fill={theme.preview.dot} listening={false} />
                ))}
              </>
            );
          })()}

          {dimDraw && activeTool === 'dimension' && (
            <Line
              points={[dimDraw.start.x, dimDraw.start.y, dimDraw.current.x, dimDraw.current.y]}
              stroke={theme.preview.stroke}
              strokeWidth={1.5}
              dash={[6, 4]}
              listening={false}
            />
          )}

          <Transformer
            ref={trRef}
            rotateEnabled
            rotateAnchorOffset={36}
            rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
            anchorSize={10}
            anchorStroke={theme.wall.selectedStroke}
            anchorFill="#ffffff"
            borderStroke={theme.wall.selectedStroke}
            borderDash={[4, 3]}
            keepRatio={false}
            anchorStyleFunc={styleRotateAnchor}
            boundBoxFunc={(oldBox, newBox) => (newBox.width < 12 || newBox.height < 12 ? oldBox : newBox)}
          />
        </Layer>
      </Stage>

      {roomDraw && activeTool === 'room' && (() => {
        const x = Math.min(roomDraw.start.x, roomDraw.current.x);
        const y = Math.min(roomDraw.start.y, roomDraw.current.y);
        const w = Math.max(1, Math.abs(roomDraw.current.x - roomDraw.start.x));
        const h = Math.max(1, Math.abs(roomDraw.current.y - roomDraw.start.y));
        const widthM = pixelsToMetres(w);
        const heightM = pixelsToMetres(h);
        const areaM2 = widthM * heightM;
        const screenX = (x + w / 2) * scale + panX;
        const screenY = (y + h) * scale + panY + 8;
        return (
          <div
            className="pointer-events-none absolute font-mono text-xs px-2 py-1 rounded shadow-lg"
            style={{
              left: screenX,
              top: screenY,
              transform: 'translate(-50%, 0)',
              backgroundColor: theme.dimensionsOverlay.background,
              color: theme.dimensionsOverlay.text,
              border: `1px solid ${theme.dimensionsOverlay.border}`,
            }}
          >
            {widthM.toFixed(2)}m × {heightM.toFixed(2)}m • {areaM2.toFixed(2)}m²
          </div>
        );
      })()}

      {wallDraw && wallDraw.length >= 1 && wallPreviewPoint && (liveSegmentM >= 0.01 || typedWallMetres) && (() => {
        const last = wallDraw[wallDraw.length - 1];
        const preview = wallPreviewPoint;
        const screenX = ((last.x + preview.x) / 2) * scale + panX;
        const screenY = ((last.y + preview.y) / 2) * scale + panY;
        return (
          <div
            className="pointer-events-none absolute font-mono text-xs px-2 py-1 rounded shadow-lg"
            style={{
              left: screenX,
              top: screenY,
              transform: 'translate(-50%, -140%)',
              backgroundColor: theme.dimensionsOverlay.background,
              color: theme.dimensionsOverlay.text,
              border: `1px solid ${theme.dimensionsOverlay.border}`,
            }}
          >
            {liveSegmentM.toFixed(2)} m
          </div>
        );
      })()}

      {roomPrompt && activeTool === 'room' && (
        <div
          className="absolute z-20"
          style={{
            left: Math.min(Math.max(8, roomPrompt.x * scale + panX), Math.max(8, stageSize.width - 240)),
            top: Math.min(Math.max(8, roomPrompt.y * scale + panY + 14), Math.max(8, stageSize.height - 200)),
          }}
        >
          <RoomSizePrompt
            onCancel={() => setRoomPrompt(null)}
            onPlace={(widthM, heightM) => commitRoom(roomPrompt.x, roomPrompt.y, metresToPixels(widthM), metresToPixels(heightM))}
          />
        </div>
      )}

      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 max-w-[36rem]"
        style={{ zIndex: 20 }}
      >
        {activeTool === 'wall' && (
          <div
            className="flex flex-wrap items-center justify-center gap-2 rounded-lg px-2 py-1.5 shadow-lg"
            style={{ backgroundColor: '#1c1c28', border: '1px solid #2e2e40' }}
          >
            <WallLinePicker thickness={wallThickness} onChange={setWallThickness} />
            {wallDraw && wallDraw.length >= 1 && (
              <WallLengthControl
                value={wallLengthDraft}
                onValueChange={setWallLengthDraft}
                pointCount={wallDraw.length}
                onCommit={commitTypedLength}
                onUndoPoint={() => undoEditor()}
                canUndo={wallDraw.length > 0}
              />
            )}
          </div>
        )}
        <div
          className="px-3 py-1 rounded-lg text-xs font-mono text-center shadow-sm"
          style={{ backgroundColor: theme.hintBar.background, color: theme.hintBar.text, border: `1px solid ${theme.hintBar.border}` }}
        >
          {wallDraw ? (
            <>
              {wallDraw.length} point{wallDraw.length !== 1 ? 's' : ''}. Type the length and press Enter — the cursor only sets the direction. Double-click to finish.
            </>
          ) : roomPrompt ? (
            'Enter width and height in metres. Escape cancels.'
          ) : roomDraw ? (
            'Release to place room'
          ) : dimDraw ? (
            'Release to place dimension'
          ) : activeTool === 'symbol' ? (
            `Click to place ${familyOf(pendingSymbolId ?? 'door')?.label ?? 'the symbol'}. It stays selected so you can drag, mirror, and copy it.`
          ) : (
            TOOL_HINTS[activeTool] ?? 'Select a tool from the left'
          )}
        </div>
      </div>
    </div>
  );
};

export default DrawingCanvas;
